import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { requestOtp } from '../../services/authService';

// وقتی پنل پیامکی از سمت API آماده شد، این فلگ را true کنید.
const OTP_LOGIN_ENABLED = false;

function LoginPage() {
  const [mode, setMode] = useState('password');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [serverOtp, setServerOtp] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { isAuthenticated, signInWithOtp, signInWithPassword } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const redirectPath = location.state?.from?.pathname || '/';

  if (isAuthenticated) {
    return <Navigate to={redirectPath} replace />;
  }

  function resetFeedback() {
    setError('');
    setMessage('');
  }

  function handleModeChange(nextMode) {
    setMode(nextMode);
    resetFeedback();
  }

  async function handlePasswordLogin(event) {
    event.preventDefault();
    resetFeedback();
    setIsLoading(true);

    try {
      await signInWithPassword({ phone, password });
      navigate(redirectPath, { replace: true });
    } catch (caughtError) {
      setError(caughtError.message);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleRequestOtp(event) {
    event.preventDefault();
    resetFeedback();
    setIsLoading(true);

    try {
      const response = await requestOtp({ phone });
      setOtpSent(true);
      setServerOtp(response.data?.otp || '');
      setMessage(response.message || 'کد یکبار مصرف ارسال شد.');
    } catch (caughtError) {
      setError(caughtError.message);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleOtpLogin(event) {
    event.preventDefault();
    resetFeedback();
    setIsLoading(true);

    try {
      await signInWithOtp({ phone, code: otpCode });
      navigate(redirectPath, { replace: true });
    } catch (caughtError) {
      setError(caughtError.message);
    } finally {
      setIsLoading(false);
    }
  }

  const inputClassName =
    'mt-2 w-full rounded-[12px] border border-slate-200 bg-[#F8F8F8] px-4 py-3 text-[13px] outline-none transition focus:border-[#914985] focus:bg-white';
  const activeTabClassName = 'bg-[#914985] text-white shadow-[0_6px_16px_rgba(145,73,133,0.25)]';
  const inactiveTabClassName = 'bg-[#F8F8F8] text-[#914985]';
  const showOtpMode = OTP_LOGIN_ENABLED && mode === 'otp';

  return (
    <div className="min-h-dvh bg-slate-100">
      <main className="mx-auto flex min-h-dvh w-full max-w-app items-center justify-center bg-white px-5 py-8 shadow-2xl shadow-slate-200/70">
        <section className="w-full rounded-[24px] bg-white p-5 shadow-[0_10px_35px_rgba(15,23,42,0.08)]">
          <div className="flex flex-col items-center text-center">
            <img className="h-[100px] w-auto" src="/images/login-logo.webp" alt="مدادرنگی" />
            <h1 className="mt-5 text-[17px] font-bold text-[#914985]">پلتفرم مدرسه هوشمند</h1>
          </div>

          {OTP_LOGIN_ENABLED && (
            <div className="mt-6 grid grid-cols-2 gap-2 rounded-[14px] bg-slate-50 p-1">
              <button
                type="button"
                className={`rounded-[12px] py-2 text-[11px] transition ${
                  mode === 'password' ? activeTabClassName : inactiveTabClassName
                }`}
                onClick={() => handleModeChange('password')}
              >
                رمز عبور
              </button>

              <button
                type="button"
                className={`rounded-[12px] py-2 text-[11px] transition ${
                  mode === 'otp' ? activeTabClassName : inactiveTabClassName
                }`}
                onClick={() => handleModeChange('otp')}
              >
                کد یکبار مصرف
              </button>
            </div>
          )}

          {!showOtpMode ? (
            <form className="mt-6 space-y-4" onSubmit={handlePasswordLogin}>
              <label className="block text-[12px] text-slate-700">
                شماره موبایل
                <input
                  className={inputClassName}
                  inputMode="numeric"
                  autoComplete="tel"
                  placeholder="مثلا 09120000001"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  required
                />
              </label>

              <label className="block text-[12px] text-slate-700">
                رمز عبور
                <input
                  className={inputClassName}
                  type="password"
                  autoComplete="current-password"
                  placeholder="رمز عبور"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
              </label>

              <button
                type="submit"
                className="w-full rounded-[12px] bg-[#00C0B9] py-2 text-[13px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-60"
                disabled={isLoading}
              >
                {isLoading ? 'در حال ورود...' : 'ورود'}
              </button>
            </form>
          ) : (
            <form className="mt-6 space-y-4" onSubmit={otpSent ? handleOtpLogin : handleRequestOtp}>
              <label className="block text-[12px] text-slate-700">
                شماره موبایل
                <input
                  className={inputClassName}
                  inputMode="numeric"
                  autoComplete="tel"
                  placeholder="مثلا 09120000001"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  required
                />
              </label>

              {otpSent && (
                <label className="block text-[12px] text-slate-700">
                  کد یکبار مصرف
                  <input
                    className={inputClassName}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="کد ارسال شده"
                    value={otpCode}
                    onChange={(event) => setOtpCode(event.target.value)}
                    required
                  />
                </label>
              )}

              <button
                type="submit"
                className="w-full rounded-[12px] bg-[#00C0B9] py-2 text-[13px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-60"
                disabled={isLoading}
              >
                {isLoading ? 'لطفا صبر کنید...' : otpSent ? 'تایید و ورود' : 'دریافت کد'}
              </button>

              {otpSent && (
                <button
                  type="button"
                  className="w-full rounded-[12px] border border-[#914985] py-2 text-[13px] text-[#914985] disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={handleRequestOtp}
                  disabled={isLoading}
                >
                  ارسال مجدد کد
                </button>
              )}
            </form>
          )}

          {message && <p className="mt-4 rounded-[12px] bg-emerald-50 px-4 py-3 text-[12px] text-emerald-700">{message}</p>}
          {serverOtp && <p className="mt-2 text-center text-[11px] text-slate-400">کد تست: {serverOtp}</p>}
          {error && <p className="mt-4 rounded-[12px] bg-red-50 px-4 py-3 text-[12px] text-red-600">{error}</p>}
        </section>
      </main>
    </div>
  );
}

export default LoginPage;
